import { usePager, Pager } from "@/components/kiyana/Pager";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID, formatTimeID } from "@/lib/format";
import { toast } from "sonner";
import {
  Search, X, Star, Users, UserPlus, Repeat2, Crown, Globe, Store, Plus,
  ShoppingCart, ShoppingBag, User, LockKeyhole, NotebookPen, Award, ChevronRight,
  Pencil, Tag as TagIcon, Trash2, Merge, Mail, Phone, CalendarDays, MapPin, ShieldCheck, Ban,
} from "lucide-react";

const STAR_LABELS = ["Belum Belanja", "Pelanggan Baru", "Regular", "Silver", "Gold", "VIP"];
const AUTO_TAG_STYLE = {
  "Baru Daftar": "bg-sky-50 text-sky-600 border-sky-200",
  "Pelanggan Baru": "bg-emerald-50 text-emerald-600 border-emerald-200",
  "Repeat Order": "bg-blue-50 text-blue-600 border-blue-200",
  "Pelanggan Aktif": "bg-teal-50 text-teal-600 border-teal-200",
  "Tidak Aktif 30 Hari": "bg-amber-50 text-amber-600 border-amber-200",
  "Tidak Aktif 90 Hari": "bg-red-50 text-red-500 border-red-200",
  "VIP": "bg-rose-50 text-rose-600 border-rose-200",
};
const MANUAL_TAG_STYLE = "bg-violet-50 text-violet-600 border-violet-200";
const CHANNEL_LABEL = { webstore: "Web Store", offline: "Toko Offline" };
const ORDER_STATUS_LABEL = {
  selesai: "Selesai", diproses: "Diproses", siap_dikirim: "Siap Dikirim", dikirim: "Dikirim",
  dibatalkan: "Dibatalkan", menunggu_pembayaran: "Menunggu Pembayaran", refund_selesai: "Refund Selesai",
  completed: "Selesai", voided: "Void",
};
const inp = "w-full h-10 px-3 rounded-lg bg-white border border-rose-200 text-sm text-slate-800 focus:outline-none focus:border-rose-400";
const initials = (n) => (n || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

function Stars({ n, size = "w-3.5 h-3.5" }) {
  return (
    <div className="flex gap-0.5" data-testid={`stars-${n}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

function TagBadge({ label, manual }) {
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${manual ? MANUAL_TAG_STYLE : (AUTO_TAG_STYLE[label] || "bg-slate-50 text-slate-500 border-slate-200")}`}>
      {label}
    </span>
  );
}

function Modal({ title, sub, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-slate-900/40 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="font-display font-bold text-slate-900">{title}</div>
            {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
          </div>
          <button data-testid={`${testid}-close`} onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tambah / Edit pelanggan
// ---------------------------------------------------------------------------
function CustomerFormModal({ title, initial, onClose, onDone, testid }) {
  const isEdit = !!initial?.id;
  const [f, setF] = useState({
    name: initial?.name || "", phone: initial?.phone || "", email: initial?.email?.endsWith("@kiyana.local") ? "" : (initial?.email || ""),
    address: "", gender: initial?.gender || "", birth_date: initial?.birth_date || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Nama wajib diisi");
    if (!isEdit && !f.phone.trim()) return toast.error("No WA wajib diisi");
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/admin/crm/customers/${initial.id}`, {
          name: f.name.trim(), phone: f.phone.trim() || null, email: f.email.trim() || null,
          gender: f.gender || null, birth_date: f.birth_date || null,
        });
        toast.success("Profil pelanggan diperbarui");
      } else {
        await api.post("/pos/customers", { name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim() || null, address: f.address.trim() || null });
        if (f.gender || f.birth_date) {
          const { data: rows } = await api.get("/admin/crm/customers", { params: { q: f.phone.trim() } });
          const created = rows.find((r) => r.phone === f.phone.trim());
          if (created) await api.put(`/admin/crm/customers/${created.id}`, { gender: f.gender || null, birth_date: f.birth_date || null });
        }
        toast.success(`Pelanggan ${f.name} tersimpan`);
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={title} sub={isEdit ? "Perubahan tercatat di audit log." : "Pelanggan offline cukup nama & No WA — tidak otomatis dibuatkan akun Web Store."} onClose={onClose} testid={testid}>
      <div className="space-y-3">
        <div><label className="text-xs text-slate-500">Nama Lengkap *</label>
          <input data-testid="cf-name" value={f.name} onChange={set("name")} className={inp} placeholder="Nama pelanggan" /></div>
        <div><label className="text-xs text-slate-500">Nomor WhatsApp {isEdit ? "" : "*"}</label>
          <input data-testid="cf-phone" value={f.phone} onChange={set("phone")} className={inp} placeholder="08xxxxxxxxxx" /></div>
        <div><label className="text-xs text-slate-500">Email (opsional)</label>
          <input data-testid="cf-email" value={f.email} onChange={set("email")} className={inp} placeholder="email@contoh.com" /></div>
        {!isEdit && (
          <div><label className="text-xs text-slate-500">Alamat (opsional)</label>
            <input data-testid="cf-address" value={f.address} onChange={set("address")} className={inp} placeholder="Alamat singkat" /></div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Jenis Kelamin (opsional)</label>
            <select data-testid="cf-gender" value={f.gender} onChange={set("gender")} className={inp}>
              <option value="">—</option><option value="wanita">Wanita</option><option value="pria">Pria</option>
            </select></div>
          <div><label className="text-xs text-slate-500">Tanggal Lahir (opsional)</label>
            <input data-testid="cf-birth" type="date" value={f.birth_date || ""} onChange={set("birth_date")} className={inp} /></div>
        </div>
        <button data-testid="cf-save" onClick={save} disabled={busy} className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
          {busy ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : "Simpan Pelanggan"}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Reset kata sandi (PIN admin) & Gabung pelanggan (PIN owner)
// ---------------------------------------------------------------------------
function ResetModal({ cust, onClose, onDone }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pin.length !== 6) return toast.error("PIN otorisasi 6 digit wajib");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/crm/customers/${cust.id}/reset-password`, { pin });
      toast.success(data.mock ? "Permintaan tercatat. OTP terkirim (simulasi — email belum aktif)." : "OTP reset kata sandi terkirim ke email pelanggan.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses");
      setPin("");
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Bantu Reset Kata Sandi" sub={`OTP dikirim ke email terverifikasi ${cust.name}. Anda tidak pernah melihat / mengatur kata sandi pelanggan.`} onClose={onClose} testid="crm-reset-modal">
      <label className="text-xs text-slate-500">PIN Otorisasi Anda</label>
      <input data-testid="crm-reset-pin" type="password" inputMode="numeric" maxLength={6} value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="••••••" className={inp + " tracking-[0.5em] font-mono text-center mb-3"} />
      <button data-testid="crm-reset-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50">
        {busy ? "Memproses..." : "Kirim OTP Reset ke Pelanggan"}
      </button>
    </Modal>
  );
}

function MergeModal({ primary, onClose, onDone }) {
  const [others, setOthers] = useState([]);
  const [secId, setSecId] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/crm/customers").then((r) => setOthers(r.data.filter((c) => c.id !== primary.id))).catch(() => {});
  }, [primary.id]);
  const submit = async () => {
    if (!secId) return toast.error("Pilih akun yang akan digabungkan");
    if (pin.length !== 6) return toast.error("PIN owner 6 digit wajib");
    setBusy(true);
    try {
      await api.post("/admin/crm/merge", { primary_id: primary.id, secondary_id: secId, pin });
      toast.success("Akun berhasil digabungkan — seluruh riwayat berpindah ke satu Customer ID");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menggabungkan");
      setPin("");
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Gabungkan Identitas Pelanggan" sub={`Akun yang dipilih akan digabung ke ${primary.name} (${primary.customer_code}). Riwayat order kedua kanal berpindah permanen & teraudit.`} onClose={onClose} testid="crm-merge-modal">
      <label className="text-xs text-slate-500">Akun kedua (akan dinonaktifkan setelah digabung)</label>
      <select data-testid="crm-merge-target" value={secId} onChange={(e) => setSecId(e.target.value)} className={inp + " mb-3"}>
        <option value="">— Pilih pelanggan —</option>
        {others.map((c) => <option key={c.id} value={c.id}>{c.name} • {c.customer_code} • {CHANNEL_LABEL[c.source]}</option>)}
      </select>
      <label className="text-xs text-slate-500">PIN Owner (otorisasi)</label>
      <input data-testid="crm-merge-pin" type="password" inputMode="numeric" maxLength={6} value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="••••••" className={inp + " tracking-[0.5em] font-mono text-center mb-3"} />
      <button data-testid="crm-merge-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold disabled:opacity-50">
        {busy ? "Memproses..." : "Gabungkan Sekarang"}
      </button>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Panel detail kanan
// ---------------------------------------------------------------------------
function DetailPanel({ id, all, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("profil");
  const [modal, setModal] = useState(null);
  const [note, setNote] = useState("");
  const [newTag, setNewTag] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/crm/customers/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Gagal memuat detail"));
  }, [id]);
  useEffect(() => { setD(null); setTab("profil"); load(); }, [load]);

  if (!d) return (
    <div className="w-[380px] flex-shrink-0 bg-white border-l border-rose-100 p-6 text-center text-xs text-slate-400">Memuat...</div>
  );
  const c = d.customer;
  const sec = d.security || {};
  const canManage = d.can_manage;

  const toggleTag = async (tagId, active) => {
    try {
      await api.post(`/admin/crm/customers/${id}/tags`, { tag_id: tagId, action: active ? "remove" : "add" });
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah tag"); }
  };
  const toggleCodBlock = async () => {
    try {
      await api.patch(`/admin/customers/${id}/cod-block`, { blocked: !sec.cod_blocked });
      toast.success(sec.cod_blocked ? "Akses COD dibuka kembali" : "Akses COD diblokir — pelanggan tidak bisa checkout COD");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah akses COD"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/crm/customers/${id}/notes`, { text: note.trim() });
      setNote(""); load();
      toast.success("Catatan tersimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan catatan"); }
  };
  const addTagType = async () => {
    if (!newTag.trim()) return;
    try {
      await api.post("/admin/crm/tags", { label: newTag.trim() });
      setNewTag(""); load(); onChanged();
      toast.success("Jenis tag dibuat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat tag"); }
  };
  const delTagType = async (tid) => {
    try {
      await api.delete(`/admin/crm/tags/${tid}`);
      load(); onChanged();
      toast.success("Jenis tag dihapus dari semua pelanggan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus tag"); }
  };

  const TABS = [
    { k: "profil", label: "Profil", Icon: User },
    { k: "order", label: "Order", Icon: ShoppingCart },
    { k: "loyalitas", label: "Loyalitas", Icon: Award },
    { k: "keamanan", label: "Keamanan", Icon: LockKeyhole },
    { k: "catatan", label: "Catatan", Icon: NotebookPen },
  ];

  const Row = ({ icon: Icon, label, value, testid }) => (
    <div className="flex items-center justify-between py-2 border-b border-rose-50 last:border-0">
      <span className="text-xs text-slate-500 flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-rose-400" /> {label}</span>
      <span data-testid={testid} className="text-xs font-medium text-slate-800 text-right max-w-[55%] truncate">{value || "—"}</span>
    </div>
  );

  return (
    <div className="w-[380px] flex-shrink-0 bg-white border-l border-rose-100 flex flex-col h-full" data-testid="crm-detail">
      <div className="p-4 border-b border-rose-50">
        <div className="flex items-start justify-between">
          <div className="font-display font-bold text-slate-900 text-sm">Detail Pelanggan</div>
          <button data-testid="crm-detail-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <div className="w-14 h-14 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-display font-bold">{initials(c.name)}</div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 text-sm truncate" data-testid="crm-detail-name">{c.name}</div>
            <div className="text-[10px] text-slate-400">{c.customer_code} • {CHANNEL_LABEL[c.source]}</div>
            <div className="text-[11px] text-slate-500 truncate">{c.email || "Tanpa email"}</div>
            <div className="text-[11px] text-slate-500">{c.phone || "Tanpa WA"}</div>
            <div className="flex items-center gap-2 mt-1">
              <Stars n={c.stars} />
              <span className="text-[10px] font-semibold text-amber-600">{c.star_label}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="bg-rose-50/60 rounded-lg p-2.5 text-center">
            <div className="text-[10px] text-slate-500 flex items-center justify-center gap-1"><ShoppingCart className="w-3 h-3" /> Total Order</div>
            <div className="font-display font-bold text-slate-900" data-testid="crm-detail-orders">{c.orders_total}</div>
          </div>
          <div className="bg-rose-50/60 rounded-lg p-2.5 text-center">
            <div className="text-[10px] text-slate-500 flex items-center justify-center gap-1"><ShoppingBag className="w-3 h-3" /> Total Belanja</div>
            <div className="font-display font-bold text-rose-600" data-testid="crm-detail-spend">{formatIDR(c.spend_net)}</div>
          </div>
        </div>
      </div>

      <div className="flex border-b border-rose-50 px-2">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`crm-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`flex-1 py-2.5 text-[11px] font-medium border-b-2 transition-colors ${tab === t.k ? "border-rose-600 text-rose-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === "profil" && (
          <div data-testid="crm-pane-profil">
            <Row icon={User} label="Customer ID" value={c.customer_code} testid="crm-prof-code" />
            <Row icon={Mail} label="Email" value={c.email} testid="crm-prof-email" />
            <Row icon={Phone} label="WhatsApp" value={c.phone} testid="crm-prof-phone" />
            <Row icon={User} label="Jenis Kelamin" value={c.gender === "wanita" ? "Wanita" : c.gender === "pria" ? "Pria" : null} />
            <Row icon={CalendarDays} label="Tanggal Lahir" value={c.birth_date ? formatDateID(c.birth_date) : null} />
            <Row icon={CalendarDays} label="Bergabung" value={formatDateID(c.created_at)} />
            <Row icon={ShieldCheck} label="Status Akun" value={c.active ? "Aktif" : "Nonaktif"} testid="crm-prof-status" />
            <Row icon={Globe} label="Kanal Belanja" value={c.channels.length ? c.channels.map((x) => CHANNEL_LABEL[x]).join(" + ") : "Belum belanja"} />
            {c.note && canManage && <div className="mt-2 bg-amber-50 border border-amber-100 rounded-lg p-2.5 text-[11px] text-amber-700">Catatan: {c.note}</div>}

            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-rose-400" /> Alamat</div>
              {d.addresses.length === 0 && <div className="text-[11px] text-slate-400">Belum ada alamat tersimpan.</div>}
              {d.addresses.map((a) => (
                <div key={a.id} className="border border-rose-100 rounded-lg p-2.5 mb-2 text-[11px] text-slate-600">
                  <div className="font-semibold text-slate-800 flex items-center gap-2">{a.name} {a.is_default && <span className="text-[9px] bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full">Utama</span>}</div>
                  <div>{a.phone}</div>
                  <div>{a.address}, {a.village}, {a.district}, {a.city}, {a.province} {a.postal_code}</div>
                </div>
              ))}
            </div>

            {canManage && (
              <div className="space-y-2 mt-4">
                <button data-testid="crm-edit-btn" onClick={() => setModal("edit")} className="w-full h-10 rounded-xl border border-rose-200 text-rose-700 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-rose-50">
                  <Pencil className="w-3.5 h-3.5" /> Edit Profil
                </button>
                <button data-testid="crm-merge-btn" onClick={() => setModal("merge")} className="w-full h-10 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-slate-50">
                  <Merge className="w-3.5 h-3.5" /> Gabungkan Identitas (Web + Offline)
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "order" && (
          <div data-testid="crm-pane-order" className="space-y-2">
            {d.history.length === 0 && <div className="text-center text-xs text-slate-400 py-8">Belum ada riwayat pembelian.</div>}
            {d.history.map((h, i) => (
              <div key={`${h.code}-${i}`} data-testid={`crm-order-${h.code}`} className="border border-rose-100 rounded-xl p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-semibold text-slate-800">{h.code}</span>
                  <span className="text-xs font-bold text-slate-900">{formatIDR(h.total)}</span>
                </div>
                <div className="text-[10px] text-slate-400 mb-1.5">
                  {formatDateID(h.created_at)} {formatTimeID(h.created_at)} • <span className={`font-semibold ${h.channel === "webstore" ? "text-sky-600" : "text-rose-500"}`}>{CHANNEL_LABEL[h.channel]}</span> • {(h.payment_method || "").toUpperCase()}
                </div>
                <div className="space-y-0.5">
                  {h.items.slice(0, 3).map((it, j) => (
                    <div key={j} className="flex justify-between text-[11px] text-slate-600">
                      <span className="truncate pr-2">{it.name} x{it.qty}</span>
                      <span>{formatIDR(it.subtotal)}</span>
                    </div>
                  ))}
                  {h.items.length > 3 && <div className="text-[10px] text-slate-400">+{h.items.length - 3} produk lainnya</div>}
                </div>
                <div className="mt-1.5"><span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{ORDER_STATUS_LABEL[h.status] || h.status}</span></div>
              </div>
            ))}
          </div>
        )}

        {tab === "loyalitas" && (
          <div data-testid="crm-pane-loyalitas">
            <div className="bg-gradient-to-br from-amber-50 to-rose-50 border border-amber-100 rounded-xl p-4 text-center mb-4">
              <Stars n={c.stars} size="w-6 h-6" />
              <div className="font-display font-bold text-slate-900 mt-1.5" data-testid="crm-loyal-label">{c.star_label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Berdasarkan frekuensi belanja 12 bulan terakhir</div>
            </div>
            <Row icon={ShoppingCart} label="Order 12 bulan terakhir" value={c.orders_12m} testid="crm-loyal-12m" />
            <Row icon={ShoppingCart} label="Total order sepanjang waktu" value={c.orders_total} />
            <Row icon={ShoppingBag} label="Total belanja bersih" value={formatIDR(c.spend_net)} />
            <Row icon={ShoppingBag} label="Rata-rata transaksi" value={formatIDR(c.avg_order)} />
            <Row icon={TagIcon} label="Produk terlaris" value={c.top_product} />

            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-800 mb-2">Tag Otomatis</div>
              <div className="flex flex-wrap gap-1.5">
                {c.auto_tags.length === 0 && <span className="text-[11px] text-slate-400">Belum ada tag.</span>}
                {c.auto_tags.map((t) => <TagBadge key={t} label={t} />)}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-800 mb-2">Tag Manual</div>
              <div className="space-y-1.5">
                {d.tag_types.map((t) => {
                  const active = (c.manual_tags || []).includes(t.label);
                  return (
                    <div key={t.id} className="flex items-center justify-between border border-rose-100 rounded-lg px-2.5 py-1.5">
                      <TagBadge label={t.label} manual />
                      {canManage && (
                        <div className="flex items-center gap-1">
                          <button data-testid={`crm-tag-toggle-${t.id}`} onClick={() => toggleTag(t.id, active)}
                            className={`text-[10px] font-semibold px-2 py-1 rounded-lg ${active ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                            {active ? "Terpasang ✓" : "Pasang"}
                          </button>
                          <button data-testid={`crm-tag-del-${t.id}`} onClick={() => delTagType(t.id)} className="text-red-300 hover:text-red-500 p-1"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {canManage && (
                <div className="flex gap-1.5 mt-2">
                  <input data-testid="crm-tag-new" value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Tag khusus baru..." className="flex-1 h-8 px-2.5 rounded-lg border border-rose-200 text-xs focus:outline-none focus:border-rose-400" />
                  <button data-testid="crm-tag-create" onClick={addTagType} className="h-8 px-3 rounded-lg bg-rose-600 text-white text-xs font-semibold"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "keamanan" && (
          <div data-testid="crm-pane-keamanan">
            {!sec.is_web_account ? (
              <div className="text-center py-8">
                <LockKeyhole className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <div className="text-xs text-slate-500">Pelanggan offline — tidak memiliki akun Web Store.</div>
                <div className="text-[10px] text-slate-400 mt-1">Bila pelanggan kehilangan akses, gunakan pemulihan identitas manual dengan persetujuan owner.</div>
              </div>
            ) : (
              <>
                <Row icon={ShieldCheck} label="Status Akun" value={sec.active ? "Aktif" : "Nonaktif"} testid="crm-sec-status" />
                <Row icon={Ban} label="Akses COD" value={sec.cod_blocked ? "Diblokir" : "Normal"} testid="crm-sec-cod" />
                <Row icon={Mail} label="Verifikasi Email" value={sec.email_verified ? "Terverifikasi" : "Belum"} />
                <Row icon={Phone} label="Verifikasi No. HP" value={sec.phone_verified ? "Terverifikasi" : "Belum"} />
                <Row icon={User} label="Metode Login" value={sec.login_method} testid="crm-sec-method" />
                <Row icon={CalendarDays} label="Login Terakhir" value={sec.last_login_at ? `${formatDateID(sec.last_login_at)} ${formatTimeID(sec.last_login_at)}` : "Belum pernah"} testid="crm-sec-login" />
                <Row icon={LockKeyhole} label="Ubah Sandi Terakhir" value={sec.password_changed_at ? `${formatDateID(sec.password_changed_at)} ${formatTimeID(sec.password_changed_at)}` : "Belum pernah"} />
                <div className="mt-4">
                  <div className="text-xs font-semibold text-slate-800 mb-2">Riwayat Permintaan Reset</div>
                  {(sec.reset_requests || []).length === 0 && <div className="text-[11px] text-slate-400">Belum ada permintaan reset.</div>}
                  {(sec.reset_requests || []).map((r) => (
                    <div key={r.id} className="text-[11px] text-slate-600 border border-rose-100 rounded-lg px-2.5 py-1.5 mb-1.5">
                      {formatDateID(r.created_at)} {formatTimeID(r.created_at)} • oleh {r.requested_by_name} • <span className="font-semibold">{r.status === "otp_sent" ? "OTP terkirim" : r.status}</span>
                    </div>
                  ))}
                </div>
                {canManage && (
                  <button data-testid="crm-reset-btn" onClick={() => setModal("reset")} className="mt-3 w-full h-10 rounded-xl border border-rose-200 text-rose-700 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-rose-50">
                    <LockKeyhole className="w-3.5 h-3.5" /> Bantu Reset Kata Sandi
                  </button>
                )}
                {canManage && (
                  <button data-testid="crm-cod-block-btn" onClick={toggleCodBlock}
                    className={`mt-2 w-full h-10 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-colors ${sec.cod_blocked ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-orange-200 text-orange-600 hover:bg-orange-50"}`}>
                    <Ban className="w-3.5 h-3.5" /> {sec.cod_blocked ? "Buka Blokir COD" : "Blokir COD"}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {tab === "catatan" && (
          <div data-testid="crm-pane-catatan">
            {!canManage ? (
              <div className="text-xs text-slate-400 text-center py-8">Catatan internal hanya untuk owner/admin.</div>
            ) : (
              <>
                <textarea data-testid="crm-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tulis catatan internal (tidak terlihat pelanggan)..." className={inp + " h-20 py-2 resize-none mb-2"} />
                <button data-testid="crm-note-save" onClick={addNote} className="w-full h-9 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold mb-4">Simpan Catatan</button>
                <div className="space-y-2">
                  {d.notes.length === 0 && <div className="text-[11px] text-slate-400 text-center py-4">Belum ada catatan.</div>}
                  {d.notes.map((n) => (
                    <div key={n.id} data-testid={`crm-note-${n.id}`} className="bg-rose-50/50 border border-rose-100 rounded-lg p-2.5">
                      <div className="text-xs text-slate-700">{n.text}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{n.author_name} • {formatDateID(n.created_at)} {formatTimeID(n.created_at)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {modal === "edit" && <CustomerFormModal title={`Edit Profil — ${c.name}`} initial={c} testid="crm-edit-modal" onClose={() => setModal(null)} onDone={() => { setModal(null); load(); onChanged(); }} />}
      {modal === "reset" && <ResetModal cust={c} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === "merge" && <MergeModal primary={c} onClose={() => setModal(null)} onDone={() => { setModal(null); onClose(); onChanged(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Halaman utama
// ---------------------------------------------------------------------------
export default function POSCustomers() {
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [tagF, setTagF] = useState("");
  const [starF, setStarF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [periodF, setPeriodF] = useState("");
  const [sortF, setSortF] = useState("newest");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [rows, setRows] = useState([]);
  const { paged: pagedRows, pager: crmPager } = usePager(rows);
  const [summary, setSummary] = useState({ total: 0, new: 0, repeat: 0, vip: 0 });
  const [tagTypes, setTagTypes] = useState([]);
  const [sel, setSel] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const periodParams = useMemo(() => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (periodF === "today") return { date_from: iso(today), date_to: iso(today) };
    if (periodF === "7d") return { date_from: iso(new Date(Date.now() - 6 * 864e5)), date_to: iso(today) };
    if (periodF === "30d") return { date_from: iso(new Date(Date.now() - 29 * 864e5)), date_to: iso(today) };
    if (periodF === "90d") return { date_from: iso(new Date(Date.now() - 89 * 864e5)), date_to: iso(today) };
    if (periodF === "month") return { date_from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), date_to: iso(today) };
    if (periodF === "custom" && customFrom && customTo) return { date_from: customFrom, date_to: customTo };
    return {};
  }, [periodF, customFrom, customTo]);

  const load = useCallback(() => {
    const params = { q, source, tag: tagF, stars: starF, status: statusF, sort: sortF, ...periodParams };
    api.get("/admin/crm/customers", { params }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/admin/crm/summary", { params: { source, ...periodParams } }).then((r) => setSummary(r.data)).catch(() => {});
  }, [q, source, tagF, starF, statusF, sortF, periodParams]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/crm/tags").then((r) => setTagTypes(r.data)).catch(() => {}); }, []);

  const CARDS = [
    { label: "Total Pelanggan", value: summary.total, Icon: Users, cls: "bg-rose-50 text-rose-500" },
    { label: "Pelanggan Baru", value: summary.new, Icon: UserPlus, cls: "bg-amber-50 text-amber-500" },
    { label: "Repeat Order", value: summary.repeat, Icon: Repeat2, cls: "bg-rose-50/70 text-rose-400" },
    { label: "VIP", value: summary.vip, Icon: Crown, cls: "bg-amber-50/80 text-amber-500" },
  ];
  const TABS = [
    { k: "all", label: "Semua", Icon: Users },
    { k: "webstore", label: "Web Store", Icon: Globe },
    { k: "offline", label: "Toko Offline", Icon: Store },
    { k: "both", label: "Kedua Kanal", Icon: Repeat2 },
  ];
  const allTagOpts = [...new Set([...Object.keys(AUTO_TAG_STYLE), ...tagTypes.map((t) => t.label)])];

  return (
    <div data-testid="pos-customers-page" className="flex h-[calc(100vh-120px)] -m-6">
      <div className="flex-1 min-w-0 overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Data Pelanggan</h1>
            <p className="text-xs text-slate-500">Kelola pelanggan dan riwayat pembelian — terintegrasi Web Store & POS offline</p>
          </div>
          <button data-testid="crm-add-btn" onClick={() => setShowAdd(true)}
            className="h-10 px-4 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold flex items-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> Tambah Pelanggan
          </button>
        </div>

        <div className="flex gap-2">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`crm-source-${t.k}`} onClick={() => setSource(t.k)}
              className={`h-10 px-4 rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors ${source === t.k ? "bg-rose-100 text-rose-700 border border-rose-200" : "bg-white text-slate-500 border border-slate-200 hover:border-rose-200"}`}>
              <t.Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {CARDS.map((cd) => (
            <div key={cd.label} data-testid={`crm-stat-${cd.label.toLowerCase().replace(/ /g, "-")}`} className="bg-white border border-rose-100 rounded-2xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cd.cls}`}><cd.Icon className="w-5 h-5" /></div>
              <div>
                <div className="text-[10px] text-slate-500">{cd.label}</div>
                <div className="font-display font-bold text-xl text-slate-900">{cd.value.toLocaleString("id-ID")}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama, email, WhatsApp atau Customer ID..."
              className="h-10 w-full pl-9 pr-3 rounded-xl bg-white border border-rose-100 text-xs focus:outline-none focus:border-rose-400" />
          </div>
          <select data-testid="crm-filter-tag" value={tagF} onChange={(e) => setTagF(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-rose-100 text-xs text-slate-600">
            <option value="">Semua Tag</option>
            {allTagOpts.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="crm-filter-stars" value={starF} onChange={(e) => setStarF(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-rose-100 text-xs text-slate-600">
            <option value="">Semua Bintang</option>
            {[0, 1, 2, 3, 4, 5].map((s) => <option key={s} value={s}>{s} Bintang — {STAR_LABELS[s]}</option>)}
          </select>
          <select data-testid="crm-filter-status" value={statusF} onChange={(e) => setStatusF(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-rose-100 text-xs text-slate-600">
            <option value="">Semua Status</option>
            <option value="active">Aktif</option>
            <option value="inactive">Nonaktif</option>
            <option value="never_bought">Belum pernah belanja</option>
          </select>
          <select data-testid="crm-filter-period" value={periodF} onChange={(e) => setPeriodF(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-rose-100 text-xs text-slate-600">
            <option value="">Semua Periode</option>
            <option value="today">Hari ini</option>
            <option value="7d">7 hari terakhir</option>
            <option value="30d">30 hari terakhir</option>
            <option value="90d">90 hari terakhir</option>
            <option value="month">Bulan ini</option>
            <option value="custom">Rentang tanggal</option>
          </select>
          <select data-testid="crm-filter-sort" value={sortF} onChange={(e) => setSortF(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-rose-100 text-xs text-slate-600">
            <option value="newest">Pelanggan terbaru</option>
            <option value="oldest">Pelanggan terlama</option>
            <option value="most_orders">Order terbanyak</option>
            <option value="fewest_orders">Order paling sedikit</option>
            <option value="highest_spend">Belanja tertinggi</option>
            <option value="lowest_spend">Belanja terendah</option>
            <option value="last_purchase">Terakhir berbelanja</option>
          </select>
        </div>
        {periodF === "custom" && (
          <div className="flex gap-2">
            <input data-testid="crm-custom-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 px-3 rounded-lg bg-white border border-rose-100 text-xs" />
            <input data-testid="crm-custom-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 px-3 rounded-lg bg-white border border-rose-100 text-xs" />
          </div>
        )}

        <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-slate-500 border-b border-rose-50 bg-rose-50/30">
                <th className="px-4 py-3 font-medium">Pelanggan</th>
                <th className="px-3 py-3 font-medium">Email / WhatsApp</th>
                <th className="px-3 py-3 font-medium text-right">Total Order</th>
                <th className="px-3 py-3 font-medium text-right">Total Belanja</th>
                <th className="px-3 py-3 font-medium">Loyalitas</th>
                <th className="px-3 py-3 font-medium">Tag</th>
                <th className="px-3 py-3 font-medium">Terakhir Belanja</th>
                <th className="px-3 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((c) => (
                <tr key={c.id} data-testid={`crm-row-${c.id}`} onClick={() => setSel(c.id)}
                  className={`border-b border-rose-50/60 cursor-pointer transition-colors ${sel === c.id ? "bg-rose-50/60" : "hover:bg-rose-50/30"}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{initials(c.name)}</span>
                      <div>
                        <div className="text-xs font-semibold text-slate-800">{c.name}</div>
                        <div className="text-[9px] text-slate-400">{c.customer_code} • {CHANNEL_LABEL[c.source]}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-[11px] text-slate-600">{c.email || "—"}</div>
                    <div className="text-[10px] text-slate-400">{c.phone || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-right text-xs font-semibold text-slate-800">{c.orders_total}</td>
                  <td className="px-3 py-3 text-right text-xs font-bold text-slate-900">{formatIDR(c.spend_net)}</td>
                  <td className="px-3 py-3"><Stars n={c.stars} /></td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[160px]">
                      {c.auto_tags.slice(0, 2).map((t) => <TagBadge key={t} label={t} />)}
                      {c.manual_tags.slice(0, 1).map((t) => <TagBadge key={t} label={t} manual />)}
                      {(c.auto_tags.length + c.manual_tags.length) > 3 && <span className="text-[9px] text-slate-400">+{c.auto_tags.length + c.manual_tags.length - 3}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-[11px] text-slate-500">{c.last_order ? formatDateID(c.last_order) : "Belum pernah"}</td>
                  <td className="px-3 py-3"><ChevronRight className="w-4 h-4 text-slate-300" /></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-xs text-slate-400">Tidak ada pelanggan yang cocok dengan filter.</td></tr>
              )}
            </tbody>
          </table>
          <Pager {...crmPager} testid="crm-pager" />
        </div>
      </div>

      {sel && <DetailPanel id={sel} all={rows} onClose={() => setSel(null)} onChanged={load} />}
      {showAdd && <CustomerFormModal title="Tambah Pelanggan" testid="crm-add-modal" onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}
